import React, { Suspense, lazy } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  HERO_SECTION_TARGETS,
  smoothScrollToSection,
} from '../utils/navigation';
import {
  ArrowDown,
  ArrowRight,
  CalendarCheck2,
  Check,
  Clock3,
  MessageCircle,
  TrendingUp,
  UserPlus,
} from 'lucide-react';

const Spline = lazy(() => import('@splinetool/react-spline'));

const outcomeIcons = [UserPlus, CalendarCheck2, Clock3, TrendingUp];

const Hero = () => {
  const { t, isRTL } = useLanguage();
  const trustItems = t('hero.trustItems');
  const outcomes = t('results.outcomes');

  return (
    <>
      <section
        className="relative isolate overflow-hidden bg-black pt-20"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent" />
        <div className="absolute left-[8%] top-[18%] h-[420px] w-[420px] rounded-full bg-[#00FFD1]/[0.055] blur-[130px] pointer-events-none" />
        <div className="absolute right-[4%] top-[8%] h-[620px] w-[620px] rounded-full bg-[#008f78]/[0.06] blur-[160px] pointer-events-none" />

        <div className="relative mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1500px] items-center gap-10 px-5 py-10 md:px-[6%] lg:grid-cols-[1.08fr_0.92fr] lg:gap-6 lg:py-6">
          <div className={`relative z-20 ${isRTL ? 'lg:order-2' : ''}`}>
            <div className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.035] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55 backdrop-blur-xl sm:text-xs">
              {t('hero.eyebrow')}
            </div>

            <h1 className="max-w-[920px] text-[clamp(3.15rem,13vw,4.25rem)] font-semibold leading-[0.9] tracking-[-0.06em] text-white lg:text-[clamp(4rem,5.5vw,6.2rem)]">
              <span className="block">{t('hero.headlineLine1')}</span>
              <span className="hero-gradient-text mt-2 block pb-[0.08em]">
                {t('hero.headlineLine2')}
              </span>
            </h1>

            <p className="mt-6 max-w-[720px] text-base leading-7 text-white/62 sm:text-lg sm:leading-8 lg:text-lg lg:leading-7">
              {t('hero.subheadline')}
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => smoothScrollToSection(HERO_SECTION_TARGETS.primary)}
                className="btn-glass-primary group w-full sm:w-auto"
              >
                <MessageCircle className="h-5 w-5" />
                {t('hero.cta1')}
                <ArrowRight
                  className={`h-5 w-5 transition-transform duration-300 ${
                    isRTL
                      ? 'rotate-180 group-hover:-translate-x-1'
                      : 'group-hover:translate-x-1'
                  }`}
                />
              </button>
              <button
                type="button"
                onClick={() => smoothScrollToSection(HERO_SECTION_TARGETS.secondary)}
                className="btn-glass-secondary group w-full sm:w-auto"
              >
                <ArrowDown className="h-5 w-5 text-[#00FFD1]" />
                {t('hero.cta2')}
              </button>
            </div>

            <div className="mt-6 grid max-w-[760px] gap-x-6 gap-y-2.5 border-t border-white/[0.08] pt-5 sm:grid-cols-2">
              {Array.isArray(trustItems) &&
                trustItems.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-2.5 text-sm leading-5 text-white/48"
                  >
                    <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full border border-[#00FFD1]/30 bg-[#00FFD1]/10">
                      <Check className="h-2.5 w-2.5 text-[#00FFD1]" />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
            </div>
          </div>

          <div
            className={`relative h-[500px] scroll-mt-24 outline-none sm:h-[620px] lg:h-[calc(100vh-9rem)] lg:min-h-[540px] lg:max-h-[700px] ${
              isRTL ? 'lg:order-1' : ''
            }`}
            aria-label={t('hero.systemLabel')}
          >
            <div className="absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00FFD1]/[0.055] blur-[110px] pointer-events-none" />
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-16 w-16 animate-spin rounded-full border-2 border-[#00FFD1] border-t-transparent" />
                </div>
              }
            >
              <div className="h-full w-full overflow-visible">
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </div>
            </Suspense>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden border-y border-white/[0.07] bg-[#050706] py-16 sm:py-20"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-[7.6923%]">
          <div className="mx-auto mb-10 max-w-4xl text-center sm:mb-12">
            <p className="text-lg font-medium tracking-[-0.02em] text-white/42 sm:text-xl">
              {t('results.line1')}
            </p>
            <h2 className="mt-2 text-3xl font-semibold tracking-[-0.045em] text-white sm:text-5xl">
              {t('results.line2')}
            </h2>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(outcomes) &&
              outcomes.map((outcome, index) => {
                const Icon = outcomeIcons[index];
                return (
                  <div
                    key={outcome}
                    className="group flex min-h-28 items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-5 py-5 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-[#00FFD1]/25 hover:bg-[#00FFD1]/[0.035]"
                  >
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl border border-white/[0.08] bg-black/40 text-[#00FFD1] transition-transform duration-500 group-hover:scale-105">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-base font-medium tracking-[-0.02em] text-white/78 sm:text-lg">
                      {outcome}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
